import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Paper, IconButton, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useApiManager from "../../utils/Api/apiManager";
import { GridToolbarExport } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { LeadIndustry, LeadRating, LeadSource, LeadStatus, AccountIndustry, AccountOwnership, AccountRating, AccountType, CampaignStatus, CampaignType, DealReasonForLoss, DealSource, DealStage, DealType } from "../../utils/enums";

// Custom toolbar to show the export button at top-right
const CustomToolbar = () => (
    <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", p: 1 }}>
        <GridToolbarExport />
    </Box>
);

const ReportViewer = () => {
    const navigate = useNavigate();
    const { reportId } = useParams(); // Get URL parameter
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [reportName, setReportName] = useState('');
    const [loading, setLoading] = useState(true);
    const { get } = useApiManager();

    const handleEnums = ({ moduleType, item }) => {
        const moduleMap = {
            0: "Account",
            1: "Campaign",
            2: "Lead", // Treat Contact as Lead
            3: "Deal",
            4: "Lead",
        };
    
        const enumMappings = {
            Deal: {
                Stage: DealStage,
                "Type": DealType,
                "Lead Source": DealSource,
                ReasonForLoss: DealReasonForLoss,
            },
            Account: {
                AccountType: AccountType,
                Industry: AccountIndustry,
                Rating: AccountRating,
                Ownership: AccountOwnership,
            },
            Campaign: {
                Type: CampaignType,
                Status: CampaignStatus,
            },
            Lead: {
                LeadSource: LeadSource,
                LeadStatus: LeadStatus,
                Industry: LeadIndustry,
                Rating: LeadRating,
            },
        };
    
        const moduleLabel = moduleMap[moduleType];
        if (!moduleLabel || !enumMappings[moduleLabel]) return item;
    
        return Object.keys(item).reduce((acc, key) => {
            if (enumMappings[moduleLabel][key]) {
                // Find label for the enum value
                const enumArray = enumMappings[moduleLabel][key];
                const enumItem = enumArray.find(e => e.value === item[key]);
                acc[key] = enumItem ? enumItem.label : item[key]; // Replace with label if found
            } else {
                acc[key] = item[key];
            }
            return acc;
        }, {});
    }; 
    
    // Fetch data from API
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const data = await get(`/reports/${reportId}/execute`);

                setReportName(data.reportName);

                const response = data.result;

                const type = data.reportType;

                if (Array.isArray(response) && response.length > 0) {
                    // Dynamically create columns from response keys
                    const dynamicColumns = Object.keys(response[0]).map((key) => ({
                        field: key,
                        headerName: key.replace(/([A-Z])/g, " $1").toUpperCase(), // Format headers (e.g., "FirstName" -> "FIRST NAME")
                        flex: 1,
                    }));

                    setColumns(dynamicColumns);

                    // Ensure each row has a unique ID
                    const processedData = response.map((item, index) => ({
                        id: index + 1,
                        ...handleEnums({ moduleType: type, item }), // Apply the function
                    }));

                    setData(processedData);
                } else {
                    setData([]);
                    setColumns([]);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [reportId]);

    return (
        <Box sx={{ p: 2, backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton onClick={() => navigate('/reports')}> 
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: "bold", ml: 1 }}>
                        {reportName || "Report Name"}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton onClick={() => window.location.reload()}>
                        <RefreshIcon />
                    </IconButton>
                    <Button variant="contained" onClick={() => navigate(`/reports/edit/${reportId}`)}>
                        Edit
                    </Button>
                </Box>
            </Box>

            {/* Data Grid with Custom Toolbar */}
            <Paper sx={{ width: "100%" }}>
                <DataGrid
                    rows={data}
                    columns={columns}
                    getRowId={(row) => row.id}
                    loading={loading}
                    pageSizeOptions={[50, 75, 100]}
                    slots={{
                        toolbar: CustomToolbar, // Use custom toolbar with export button
                    }}
                    sx={{ backgroundColor: "white" }}
                />
            </Paper>
        </Box>
    );
};

export default ReportViewer;
